<template>
    <div class="d-flex flex-column" id="p_checking_account">
        <v-overlay :value="isLoading">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>

        <v-row ref="headerFilters">
            <data-table-filters v-if="!isMobile"
                :name="tb('Conta Corrente')"
                isCheckingAccount
                :generateRefHeaders="generate_ref_headers"
                :has-active-filters="hasActiveFilters"
                :filters="filters"
                :schema="tSchema(schema)"
                @expand-active-filters="hasExpandedActiveFilters = $event"
                @remove-filter="removeFilter"
                @refresh="refresh"
                @clear-filters="onClearFilters()"
                @open-checking-account-dialog="isCheckingAccountDialogOpen = true"
            >
            </data-table-filters>
            <div class="col wrap-title-filters" v-else >
                <filters-dialog
                    :name="tb('Conta Corrente')"
                    isCheckingAccount
                    :generateRefHeaders="generate_ref_headers"
                    :has-active-filters="hasActiveFilters"
                    :filters="filters"
                    :schema="tSchema(schema)"
                    @expand-active-filters="hasExpandedActiveFilters = $event"
                    @remove-filter="removeFilter"
                    @refresh="refresh"
                    @clear-filters="onClearFilters()"
                    @open-checking-account-dialog="isCheckingAccountDialogOpen = true"
                    >
                </filters-dialog>
            </div>
        </v-row>

        <v-row>
            <v-col cols="12">
                <pure-datatable endpoint="/api/financial/checking-account" :queryParams="queryParams" :ready="ready" ref="datatable" @data-changed="dataChanged" v-slot:default="{ vOn, vBind, isLoading }">
                    <div>
                        <portal to="v-form-base">
                            <v-form-base :model="filters" :schema="tSchema(schema)" @input="onFiltersChanged"> </v-form-base>
                        </portal>

                        <v-data-table id="checking_account"
                            class="elevation-1 d-flex flex-column justify-space-between no-extra-padding-data-table"
                            :class="{ 'nowrap-headers': $vuetify.breakpoint.width >= 1550 }"
                            :style="`border-radius: 16px; height: ${datatableSize}px;`"
                            fixed-header
                            multi-sort
                            :headers="tHeaders(headers)"
                            :header-props="headerProps"
                            :hide-default-headers="isMobile"
                            @update:options="refresh"
                            v-model="selectedRows"
                            v-on="vOn"
                            v-bind="vBind"
                            hide-default-footer
                            :show-select="showSelectMultiXml"
                            :no-data-text="$t('table.noData')"
                            :no-results-text="$t('table.noResults')"
                            :group-by="'branch_id'"
                            :loading="isMobile?isLoading:false"
                            :loading-text="$t('table.loading')"
                            :footer-props="{
                                showFirstLastPage: false,
                                itemsPerPageOptions: [10, 20, 50],
                                itemsPerPageText: $t('table.itemsPerPage'),
                            }"
                        >
                        <template v-if="!isMobile" v-slot:item.data-table-select="{item, on, props, select }" >
                            <v-simple-checkbox class="seleted-item" v-if="hasInvoiceXml(item) && !item.header" :value="checkIfSelected(item)" @input="select($event)"></v-simple-checkbox>
                        </template>
                            <template #[`group.header`]="{ items, isOpen, toggle }">
                                <th colspan="100%">
                                    <v-icon @click="toggle">{{ isOpen ? 'mdi-minus' : 'mdi-plus' }} </v-icon>

                                    <v-icon :class="getBranchColor(items[0])" class="appbar-icon-button-smaller elevation-1 mr-1 no-background-hover dark-blue-color">
                                        {{ getBranchIcon(items[0]) }}
                                    </v-icon>

                                    <span v-if="items[0].consolidator && !isMobile">{{ tb('Consolidação de valores do cliente: ') }}</span>
                                    <div  style="min-width: 240px;display: inline-block;" v-if="items[0].consolidator && isMobile">{{ tb('Consolidação de valores do cliente: ') }}</div>
                                    <span>{{ items[0].branch_name }}</span>
                                    <span v-if="items[0].consolidator && userStore.hasPermissionTo('View client and branch number')">&nbsp;({{ items[0].client_number }})</span>
                                    <span v-if="!items[0].consolidator && userStore.hasPermissionTo('View client and branch number')">&nbsp;({{ items[0].branch_id }})</span>
                                </th>
                            </template>

                            <template v-if="!isMobile" #[`item.document_id`]="{ item }">
                                <div v-if="!item.header" class="d-flex justify-start align-center">
                                    <div style="cursor: pointer">
                                        {{ item.document_id }}
                                    </div>
                                </div>
                            </template>

                            <template v-if="!isMobile" #[`item.invoice_date`]="{ item }">
                                <div v-if="!item.header" class="d-flex justify-center align-center">
                                    <div style="cursor: pointer">
                                        {{ formatDate(item.invoice_date) }}
                                    </div>
                                </div>
                            </template>

                            <template v-if="!isMobile" #[`item.total_value`]="{ item }">
                                <div class="d-flex justify-start align-center">
                                    <span v-if="item.consolidator" class="font-weight-bold">
                                        {{ formatPrice(item.consolidated_total_value) }}
                                    </span>

                                    <span v-else-if="item.header" class="font-weight-bold">
                                        {{ formatPrice(item.sum_total_value) }}
                                    </span>

                                    <span v-else>
                                        {{ formatPrice(item.total_value) }}
                                    </span>
                                </div>
                            </template>

                            <template v-if="!isMobile" #[`item.due_date`]="{ item }">
                                <div v-if="!item.header" class="d-flex justify-center align-center">
                                    <div style="cursor: pointer">
                                        {{ formatDate(item.due_date) }}
                                    </div>
                                </div>
                            </template>

                            <template v-if="!isMobile" #[`item.due_days`]="{ item }">
                                <div v-if="!item.header" class="d-flex align-center content-center items-center justify-center">
                                    <v-icon v-if="item.due_days > 0 && item.open_value != 0" small class="red-color mr-1">mdi-alert</v-icon>

                                    <div :class="{ 'red--text': item.due_days != 0 }">
                                        <span v-if="item.due_days > 0 && item.open_value != 0">{{ item.due_days }}</span>
                                    </div>
                                </div>
                            </template>

                            <template v-if="!isMobile" #[`item.open_value`]="{ item }">
                                <div class="d-flex justify-start align-center">
                                    <span v-if="item.consolidator" class="font-weight-bold">
                                        {{ formatPrice(item.consolidated_open_value) }}
                                    </span>

                                    <span v-else-if="item.header" class="font-weight-bold">
                                        {{ formatPrice(item.sum_open_value) }}
                                    </span>

                                    <span v-else :class="{ 'red--text': item.due_days > 0 }">
                                        <span v-if="item.open_value != 0">{{ formatPrice(item.open_value) }}</span>
                                    </span>
                                </div>
                            </template>

                            <template v-if="!isMobile" #[`item.accumulated_value`]="{ item }">
                                <div v-if="!item.header" class="d-flex justify-start align-center">
                                    {{ formatPrice(item.accumulated_value) }}
                                </div>
                            </template>

                            <template v-if="!isMobile" #[`item.actions`]="{ item }">
                                <div v-if="!item.header" class="d-flex justify-end align-center">
                                    <!--<v-tooltip bottom open-on-hover content-class="myprovet-tooltip" v-if="hasInvoiceAttached(item) && hasPsico(item)">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color ml-1" v-on="on" v-bind="attrs" style="cursor: pointer" @click="downloadInvoice(item, true)">mdi-lock-outline</v-icon>
                                        </template>

                                        <span>{{ tb('Requisição Substâncias Psicoativas') }}</span>
                                    </v-tooltip>-->
                                    <v-tooltip bottom open-on-hover content-class="myprovet-tooltip" v-if="hasInvoiceXml(item)">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color ml-1" v-on="on" v-bind="attrs" style="cursor: pointer" @click="downloadXmlInvoice(item)">  mdi-xml</v-icon>
                                        </template>

                                        <span>{{ tb('Descarregar fatura em XML') }}</span>
                                    </v-tooltip>


                                    <v-tooltip bottom open-on-hover content-class="myprovet-tooltip" v-if="hasInvoiceAttached(item)">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color ml-1" v-on="on" v-bind="attrs" style="cursor: pointer" @click="downloadInvoice(item)">mdi-download</v-icon>
                                        </template>

                                        <span>{{ tb('Descarregar fatura em PDF') }}</span>
                                    </v-tooltip>

                                    <v-tooltip bottom open-on-hover content-class="myprovet-tooltip" v-if="userStore.hasPermissionTo('Generate payment reference') && isInvoice(item) && !wasPaid(item)">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color ml-1" v-on="on" v-bind="attrs" style="cursor: pointer" @click="openPaymentMethodDialog(item)"> mdi-currency-eur </v-icon>
                                        </template>
                                        <span>{{ tb('Gerar pagamento') }}</span>
                                    </v-tooltip>

                                    <v-tooltip bottom open-on-hover content-class="myprovet-tooltip" v-if="userStore.hasPermissionTo('Generate payment reference') && isInvoice(item) && wasPaid(item)">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small class="appbar-icon-button-small btn-bg-color-blue green-color ml-1" v-on="on" v-bind="attrs" style="cursor: default"> mdi-check-bold </v-icon>
                                        </template>
                                        <span>{{ tb('Pago') }}</span>
                                    </v-tooltip>
                                </div>
                            </template>
                            <template v-if="isMobile" v-slot:item="{ item, index }">
                                <tr>
                                    <v-card
                                    elevation="2"
                                    >
                                    <v-row v-if="!item.header">
                                        <v-col class="d-flex flex-column col pt-0 pb-0">
                                            <v-row class="d-flex align-start pt-0 pb-0">
                                                <v-col class="col col-4 px-0 pt-0 pb-0 wrap-l">
                                                    <v-col class="col col-12 px-0 pt-0 pb-0 field" style="min-height: 44px;">{{ tb('Nº DOCUMENTO') }}</v-col>
                                                    <v-col class="col col-12 px-0 pt-0 pb-0 field">{{ tb('DATA DOC.') }}</v-col>
                                                    <v-col class="col col-12 px-0 pt-0 pb-0 field">{{ tb('VALOR TOTAL') }}</v-col>
                                                    <v-col class="col col-12 px-0 pt-0 pb-0 field">{{ tb('VENCIMENTO') }}</v-col>
                                                    <v-col class="col col-12 px-0 pt-0 pb-0 field invoices" v-if="item.due_days > 0 && item.open_value != 0" >{{ tb('DIAS ATRASO') }}</v-col>
                                                    <v-col class="col col-12 px-0 pt-0 pb-0 field invoices">{{ tb('VALOR ABERTO') }}</v-col>
                                                </v-col>
                                                <v-col class="col col-8  pt-0 pb-0 wrap-r">
                                                    <v-col class="col col-12 px-0 pt-0 pb-0">
                                                        <v-col class="d-flex px-0 mx-0 pt-0 pb-0 flex-column" style="min-height: 44px;">
                                                            <v-col class="d-flex align-start px-0 pt-0 pb-0">
                                                                <v-col class="col col-8 px-0 pt-0 pb-0 field-r ">
                                                                    <div class="datatable-designation-text">
                                                                        {{ item.document_id }}
                                                                     </div>
                                                                </v-col>
                                                                <v-col class="col d-flex col-4 px-0 pt-0 pb-0">
                                                                    <!--<v-tooltip bottom open-on-hover content-class="myprovet-tooltip" v-if="hasInvoiceAttached(item) && hasPsico(item)">
                                                                        <template v-slot:activator="{ on, attrs }">
                                                                            <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color ml-1" v-on="on" v-bind="attrs" style="cursor: pointer" @click="downloadInvoice(item, true)">mdi-lock-outline</v-icon>
                                                                        </template>

                                                                        <span>{{ tb('Requisição Substâncias Psicoativas') }}</span>
                                                                    </v-tooltip>-->
                                                                    <v-tooltip bottom open-on-hover content-class="myprovet-tooltip" v-if="hasInvoiceAttached(item)">
                                                                        <template v-slot:activator="{ on, attrs }">
                                                                            <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color ml-1" v-on="on" v-bind="attrs" style="cursor: pointer" @click="downloadInvoice(item)">mdi-download</v-icon>
                                                                        </template>

                                                                        <span>{{ tb('Descarregar fatura') }}</span>
                                                                    </v-tooltip>

                                                                    <v-tooltip bottom open-on-hover content-class="myprovet-tooltip" v-if="userStore.hasPermissionTo('Generate payment reference') && isInvoice(item) && !wasPaid(item)">
                                                                        <template v-slot:activator="{ on, attrs }">
                                                                            <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color ml-1" v-on="on" v-bind="attrs" style="cursor: pointer" @click="openPaymentMethodDialog(item)"> mdi-currency-eur </v-icon>
                                                                        </template>
                                                                        <span>{{ tb('Gerar pagamento') }}</span>
                                                                    </v-tooltip>
                                                                    <v-tooltip bottom open-on-hover content-class="myprovet-tooltip" v-if="userStore.hasPermissionTo('Generate payment reference') && isInvoice(item) && wasPaid(item)">
                                                                        <template v-slot:activator="{ on, attrs }">
                                                                            <v-icon small class="appbar-icon-button-small btn-bg-color-blue green-color ml-1" v-on="on" v-bind="attrs" style="cursor: default"> mdi-check-bold </v-icon>
                                                                        </template>
                                                                        <span>{{ tb('Pago') }}</span>
                                                                    </v-tooltip>
                                                                </v-col>
                                                            </v-col>
                                                        </v-col>
                                                    </v-col>
                                                    <v-col class="col col-12 px-0 pt-0 pb-0">
                                                        <v-col class="d-flex px-0 mx-0 pt-0 pb-0 flex-column">
                                                            <v-col class="d-flex align-start px-0 pt-0 pb-0">
                                                                <v-col class="col col-8 px-0 pt-0 pb-0 field-r ">
                                                                    <div class="datatable-designation-text">
                                                                        {{formatDate(item.invoice_date)}}
                                                                     </div>
                                                                </v-col>
                                                                <!--<v-col class="col d-flex col-4 px-0 pt-0 pb-0">
                                                                    <v-tooltip bottom open-on-hover content-class="myprovet-tooltip" v-if="hasInvoiceAttached(item) && hasPsico(item)">
                                                                        <template v-slot:activator="{ on, attrs }">
                                                                            <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color ml-1" v-on="on" v-bind="attrs" style="cursor: pointer" @click="downloadInvoice(item, true)">mdi-lock-outline</v-icon>
                                                                        </template>

                                                                        <span>{{ tb('Requisição Substâncias Psicoativas') }}</span>
                                                                    </v-tooltip>
                                                                </v-col>-->
                                                            </v-col>
                                                        </v-col>
                                                    </v-col>
                                                   <!-- <v-col class="col col-12 px-0 pt-0 pb-0 field-r">
                                                        <div class="datatable-designation-text">
                                                            {{ formatDate(item.invoice_date) }}
                                                         </div>
                                                    </v-col>-->
                                                    <v-col class="col col-12 px-0 pt-0 pb-0 field-r">
                                                        <div class="datatable-designation-text">
                                                            <span v-if="item.consolidator" class="font-weight-bold">
                                                                {{ formatPrice(item.consolidated_total_value) }}
                                                            </span>

                                                            <span v-else-if="item.header" class="font-weight-bold">
                                                                {{ formatPrice(item.sum_total_value) }}
                                                            </span>

                                                            <span v-else>
                                                                {{ formatPrice(item.total_value) }}
                                                            </span>
                                                        </div>
                                                    </v-col>
                                                    <v-col class="col col-12 px-0 pt-0 pb-0 field-r">
                                                        <div class="datatable-designation-text">
                                                                {{ formatDate(item.due_date) }}
                                                        </div>
                                                    </v-col>
                                                    <v-col class="col col-12 px-0 pt-0 pb-0 field-r"  v-if="item.due_days > 0 && item.open_value != 0">
                                                        <div class="datatable-designation-text">
                                                            <div v-if="!item.header" class="d-flex align-center content-center items-center justify-center">
                                                                <v-icon v-if="item.due_days > 0 && item.open_value != 0" small class="red-color mr-1">mdi-alert</v-icon>

                                                                <div :class="{ 'red--text': item.due_days != 0 }">
                                                                    <span v-if="item.due_days > 0 && item.open_value != 0">{{ item.due_days }}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </v-col>
                                                    <v-col class="col col-12 px-0 pt-0 pb-0 field-r">
                                                        <div class="datatable-designation-text">
                                                            <div class="d-flex justify-start align-center">
                                                                <span v-if="item.consolidator" class="font-weight-bold">
                                                                    {{ formatPrice(item.consolidated_open_value) }}
                                                                </span>

                                                                <span v-else-if="item.header" class="font-weight-bold">
                                                                    {{ formatPrice(item.sum_open_value) }}
                                                                </span>

                                                                <span v-else :class="{ 'red--text': item.due_days > 0 }">
                                                                    <span v-if="item.open_value != 0">{{ formatPrice(item.open_value) }}</span>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </v-col>
                                                    <v-col class="col col-12 px-0 pt-0 pb-0 field-r">
                                                        <div class="datatable-designation-text">
                                                            <div v-if="!item.header" class="d-flex justify-start align-center">
                                                                {{ formatPrice(item.accumulated_value) }}
                                                            </div>
                                                        </div>
                                                    </v-col>

                                                </v-col>
                                            </v-row>
                                        </v-col>
                                    </v-row>
                                    <v-row v-else>
                                        <v-col class="d-flex flex-column col pt-0 pb-0">
                                            <v-row class="d-flex align-start pt-0 pb-0">
                                                <v-col class="col col-4 px-0 pt-0 pb-0 wrap-l">

                                                    <v-col class="col col-12 px-0 pt-0 pb-0 field">{{ tb('VALOR TOTAL') }}</v-col>
                                                    <v-col class="col col-12 px-0 pt-0 pb-0 field invoices">{{ tb('VALOR ABERTO') }}</v-col>
                                                </v-col>
                                                <v-col class="col col-8  pt-0 pb-0 wrap-r">

                                                    <v-col class="col col-12 px-0 pt-0 pb-0 field-r">
                                                        <div class="datatable-designation-text">
                                                            <span v-if="item.consolidator" class="font-weight-bold">
                                                                {{ formatPrice(item.consolidated_total_value) }}
                                                            </span>

                                                            <span v-else-if="item.header" class="font-weight-bold">
                                                                {{ formatPrice(item.sum_total_value) }}
                                                            </span>

                                                            <span v-else>
                                                                {{ formatPrice(item.total_value) }}
                                                            </span>
                                                        </div>
                                                    </v-col>
                                                    <v-col class="col col-12 px-0 pt-0 pb-0 field-r">
                                                        <div class="datatable-designation-text">
                                                            <div class="d-flex justify-start align-center">
                                                                <span v-if="item.consolidator" class="font-weight-bold">
                                                                    {{ formatPrice(item.consolidated_open_value) }}
                                                                </span>

                                                                <span v-else-if="item.header" class="font-weight-bold">
                                                                    {{ formatPrice(item.sum_open_value) }}
                                                                </span>

                                                                <span v-else :class="{ 'red--text': item.due_days > 0 }">
                                                                    <span v-if="item.open_value != 0">{{ formatPrice(item.open_value) }}</span>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </v-col>

                                                </v-col>
                                            </v-row>
                                        </v-col>
                                    </v-row>
                                    </v-card>
                                </tr>
                            </template>
                            <template v-slot:footer="scope">
                                <div>
                                    <div class="datatable-footer-border"></div>

                                    <data-table-footer :showExportXMLButton="canExportXml()" @xml="exportXmlData" :showExport="canExport()" :scope="scope" @export="exportData" @updateOptions="refresh" :showOrderButton="false" :isLoading="isLoading" style="height: fit-content"> </data-table-footer>
                                </div>
                            </template>
                        </v-data-table>
                    </div>
                </pure-datatable>
            </v-col>
        </v-row>

        <send-checking-account-dialog
            v-if="isCheckingAccountDialogOpen"
            :filters="filters"
            :schema="tSchema(schema)"
            :queryParams="queryParams"
            :value="isCheckingAccountDialogOpen"
            @on-cancel="isCheckingAccountDialogOpen = false"
            @on-success="isCheckingAccountDialogOpen = false"
        ></send-checking-account-dialog>

        <payment-method-dialog
            :key="selectedCheckingAccount.document_number"
            @on-success="refresh()"
            @input="handleIsPaymentMethodDialogOpen($event)"
            :isPaymentMethodDialogOpen="isPaymentMethodDialogOpen"
            :serie="selectedCheckingAccount.series"
            :document_number="selectedCheckingAccount.document_number"
            :document_id="selectedCheckingAccount.document_id"
            :invoice_id="selectedCheckingAccount.invoice_id"
            :total="selectedCheckingAccount.open_value"
            :sendPaymentEmail="true"
        ></payment-method-dialog>
    </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useUserStore } from '@/Stores/user';
import { useBranchStore } from '@/Stores/branch';
import { useCategoriesStore } from '@/Stores/categories';
import { useAppStore } from '@/Stores/app';
import { axios } from '@/Services/Backend';
import PureDatatable from '@arentia/atools/PureDatatable';
import MainLayout from './../../Layouts/MainLayout';
import VFormBase from 'vuetify-form-base';
import ResizeObserver from 'resize-observer-polyfill';
import { list } from 'postcss';

export default {
    layout: MainLayout,

    components: {
        VFormBase,
        PureDatatable,
    },

    props: {
        model: [Object, Array],
        schema: [Object, Array],
        headers: [Object, Array],
        generate_ref_headers: [Object, Array],
    },

    data: () => ({
        isLoading: false,

        datatableSize: 200,
        resizeObserver: null,

        filters: {},
        queryParams: {},
        ready: false,
        hasExpandedActiveFilters: false,

        selectedCheckingAccount: {},

        isPaymentMethodDialogOpen: false,
        isCheckingAccountDialogOpen: false,
        isOrderDetailsDialogOpen: false,
        first:false,
        currentConsolidatedStatus:null,
        isBranchesWithXml:false,
        showSelectMultiXml:false,
        selectedRows:[]


    }),

    created() {
        if(this.isMobile){
            this.getPrefilledBranches()
        }else{
            this.applyDefaultFilters();
        }
    },

    mounted() {
        this.prepareResponsiveBehaviour();
        this.refresh();
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.computeDatatableSize);
        this.resizeObserver?.unobserve(this.$refs.headerFilters);
    },

    watch: {
        'branchStore.branch': {
            handler: function (branch) {
                if (!branch) return;

                this.queryParams.branch_id = branch.id;

                this.ready = true;
                if(!this.isMobile){
                    this.refresh();
                }else{
                    if(this.first){
                        this.refresh();
                    }else{
                        this.getPrefilledBranches()
                    }
                }
            },
            immediate: true,
        },
    },

    computed: {
        ...mapStores(useUserStore, useBranchStore, useCategoriesStore,useAppStore),

        hasActiveFilters() {
            return Object.values(this.filters)
                .filter((x) => x) // ignore null or undefined
                .some((x) => (typeof x === 'string' && x !== '') || (Array.isArray(x) && x.length > 0) || (typeof x === 'object' && Object.keys(x).length > 0));
        },
        isMobile(){
            return this.appStore.mobile_ls?false:this.appStore.is_mobile;
        },
        headerProps(){
            return this.appStore.header_props
        }

    },

    methods: {
        getPrefilledBranches(){
             axios
                .get('/api/branches/prefilled-branches')
                .then((response) => {
                       let result = response.data
                       this.model['branches##id']=[];
                       for(let field in result){
                          this.model['branches##id'].push({id:result[field].id})
                       }
                       this.applyDefaultFilters();

                       if(!this.first){
                            this.$nextTick(_=>{
                                this.first=true
                                this.refresh()
                            })
                       }


                }).catch((error) => {
                    console.log(error);
                });
        },
        handleIsPaymentMethodDialogOpen(isOpen) {
            if (isOpen === 'closedWithNoMethod') isOpen = false;

            this.isPaymentMethodDialogOpen = isOpen;
        },

        dataChanged() {
            const wrapper = this.$refs['datatable']?.$el?.querySelector('div.v-data-table__wrapper');
            this.scrollDatatableToTop(this.$vuetify, wrapper);
        },

        getBranchIcon(branch) {
            if (branch.consolidator) return 'mdi-alpha-c';

            return branch.is_petfood ? 'mdi-alpha-p' : 'mdi-alpha-f';
        },
        getBranchColor(branch) {
            if (branch.consolidator) return 'btn-bg-color-blue';

            return branch.is_petfood ? 'btn-bg-color-navbar-petfood-inactive' : 'btn-bg-color-blue';
        },
        checkIfSelected(item){
           let rs = _.find(this.selectedRows,item,{id:item.id})
           if(rs){
             return true
           }
           return false

        },

        downloadXmlInvoice(item, download_psico = false) {
            this.isLoading = true;

            this.selectedCheckingAccount = item;
            const url = '/api/financial/checking-account/xml-invoice';
            var fileName = _.replace(item.document_id,' ','_')+'.xml';
            var fileType = '.xml';
            axios({
                url,
                responseType: 'blob',
                params: {
                    branch_id: item.branch_id,
                    document_id: item.document_id,
                    due_date: item.due_date,
                    download_psico: download_psico,
                },
            })
                .then((response) => {
                    let fileURL = window.URL.createObjectURL(new Blob([response.data], { type: fileType  }));
                    let fileLink = document.createElement('a');

                    fileLink.href = fileURL;
                    fileLink.download = fileName;

                    fileLink.click();
                })
                .catch(() => {
                    this.showError(this.tb('Não foi possível descarregar o documento.'));
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        downloadMultiXmlInvoice() {
            this.isLoading = true;
            let data=[];

            _.each(this.selectedRows,(o)=>{
                let dueDate = new Date(o.due_date)
                let dueDateY = dueDate.getFullYear();
                let params={
                        branch_id: o.branch_id,
                        documents:o.document_id,
                        year:dueDateY
                }
                let index = _.findIndex(data, {branch_id:o.branch_id,year:dueDateY})
                if(index!=-1){
                    data[index].documents=data[index].documents+','+o.document_id
                }else{
                    data.push(params)
                }
            })

            var date = new Date()
            var time = this.formatDateTime(date)
            const url = '/api/financial/checking-account/xml-invoice-zip';
            var fileName = 'myprovet_faturas_xml_'+ time +'.zip';

            axios({
                method: 'POST',
                url,
                responseType: 'blob',
                params: {
                    data:data
                },
            }).then((response) => {
                    let fileURL = window.URL.createObjectURL(new Blob([response.data]));
                    let fileLink = document.createElement('a');
                    fileLink.href = fileURL;
                    fileLink.download = fileName;

                    fileLink.click();
                    this.$nextTick(_=>(
                        this.selectedRows=[]
                    ))
                })
                .catch(() => {
                    this.showError(this.tb('Não foi possível descarregar o ficheiro.'));
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        downloadInvoice(item, download_psico = false) {
            this.isLoading = true;

            this.selectedCheckingAccount = item;
            const url = '/api/financial/checking-account/invoice';

            axios({
                url,
                responseType: 'blob',
                params: {
                    branch_id: item.branch_id,
                    document_id: item.document_id,
                    download_psico: download_psico,
                },
            })
                .then((response) => {
                    let fileURL = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    let fileLink = document.createElement('a');

                    fileLink.href = fileURL;
                    fileLink.download = !download_psico ? item.document_id : item.document_id + '-psico';

                    fileLink.click();
                })
                .catch(() => {
                    this.showError(this.tb('Não foi possível descarregar o documento.'));
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        prepareResponsiveBehaviour() {
            const makeResponsive = () => {
                this.computeDatatableSize();
            };

            this.resizeObserver = new ResizeObserver(makeResponsive);
            this.resizeObserver.observe(this.$refs.headerFilters);

            window.addEventListener('resize', makeResponsive);

            makeResponsive();
        },

        computeDatatableSize() {
            const topElement = this.$refs.headerFilters;

            if (topElement) {
                const topHeight = topElement.getBoundingClientRect().bottom;
                const res = window.innerHeight - 75 - topHeight;
                this.datatableSize = res;
            }
        },

        removeFilter(filters, index) {
            filters.splice(index, 1);
            this.refresh();
        },

        onFiltersChanged() {
            if(this.filters.consolidated == "Não Consolidar"){
                this.currentConsolidatedStatus=this.filters.consolidated
            }
            let hasAutoSearch = this.userStore.user?.auto_search_disabled
            if(this.isIOS()){
                if(!hasAutoSearch){
                    this.refresh();
                }
            }else{
                this.refresh();
            }
        },
        refresh(options) {
            if (!this.branchStore.branch) return;

            if (this.$refs.datatable) {
                if (options) {
                    this.$refs.datatable.setLocalOptions(options);
                }
                this.$refs.datatable.onChangeQueryBuilder(this.filters, this.schema);
            }
        },

        applyDefaultFilters() {
            for (let field in this.model) {
                this.$set(this.filters, field, this.model[field]);
            }
        },

        isInvoice(item) {
            // @Note: Only unpaid invoives and unpaid credit notes can be paid.
            return item.series.startsWith('FT');
        },

        wasPaid(item) {
            let wasPaid = false;
            if(item.open_value==0){
                return true
            }
            if (item.latest_payment) wasPaid = item.latest_payment.was_paid;
            if (item.latest_phc_payment && !wasPaid) wasPaid = item.latest_phc_payment.was_paid;

            return wasPaid;
        },

        hasInvoiceAttached(item) {
            return !item.series.startsWith('RE');
        },
        hasInvoiceXml(item) {
            if(item.is_invoice_cius){
                let dateNow= new Date();
                let invoiceDate= new Date(item.invoice_date)
                let dateAvailabled = invoiceDate.setDate(invoiceDate.getDate()+1);
                if(dateNow >= dateAvailabled){
                    if(this.currentConsolidatedStatus=="Não Consolidar"){
                        this.$nextTick(_=>{
                            this.showSelectMultiXml=true
                        })
                    }

                    return  true;
                }
            }
            return  false;
        },
        hasInvoice(item) {
            return !item.series.startsWith('RE');
        },

        hasPsico(item) {
            return item.has_psico ?? false;
        },

        onClearFilters() {
            this.applyDefaultFilters();
            this.hasExpandedActiveFilters = false;

            this.refresh();
        },

        openPaymentMethodDialog(checking_account) {
            this.selectedCheckingAccount = checking_account;
            this.isPaymentMethodDialogOpen = true;
        },

        canExport() {
            return this.userStore.hasPermissionTo('Export financial details');
        },

        canExportXml() {
            return _.size(this.selectedRows)>0?true:false;
        },
        exportXmlData(){
          this.downloadMultiXmlInvoice();
        },
        exportData() {
            if (!this.branchStore.branch || !this.$refs.datatable) return;

            const axiosSettings = {
                url: '/api/financial/checking-accounts/export',
                params: {
                    branch_id: this.branchStore.branch.id,
                },
            };

            this.$refs.datatable.exportData(axiosSettings, 'export_conta_corrente');
        },
    },
};
</script>

<style>
.max-pagination-height > .v-pagination {
    height: 24px !important;
}
.max-pagination-height > .v-pagination .v-pagination__item {
    font-size: 12px !important;
}

.v-text-field {
    margin-top: 0 !important;
}

.v-text-field input {
    padding: 4px 0 !important;
    margin-top: 0 !important;
}

#form-base > div > .v-input > .v-input__control > .v-input__slot .v-input__icon--prepend-inner button.v-icon {
    color: #154579;
    font-size: 18px !important;
}
</style>
